import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

/** Safely extract a display string from an axios error.
 *  Handles FastAPI 422 responses where detail is an array of {msg, loc, ...} objects. */
export const apiErrMsg = (err, fallback = "Something went wrong") => {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => (x?.msg || x?.message || JSON.stringify(x))).join("; ") || fallback;
  if (typeof d === "object") return d.msg || d.message || JSON.stringify(d);
  return fallback;
};

// Auto-retry once with exponential backoff on 502/503/504 — handles Kubernetes
// cold-start blips that would otherwise surface as a "Cloudflare origin"
// error to the user. Only GETs, and only once (config.__retryCount guard).
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const cfg = error?.config;
    const status = error?.response?.status;
    const isRetriable = [502, 503, 504].includes(status) || error?.code === "ECONNABORTED";
    if (cfg && isRetriable && (cfg.method || "get").toLowerCase() === "get" && !cfg.__retryCount) {
      cfg.__retryCount = 1;
      await new Promise((res) => setTimeout(res, 1200));
      try {
        return await api.request(cfg);
      } catch (e) {
        // Fall through to reject — decorate with the failing URL so the caller
        // can display exactly which endpoint died (removes the guesswork).
        e.__failedUrl = cfg.url;
        return Promise.reject(e);
      }
    }
    if (error) error.__failedUrl = cfg?.url;
    return Promise.reject(error);
  },
);

// Optional: expose a helper that renders a diagnostic toast for HTML-body errors
// (e.g. Cloudflare error pages) so the user sees WHICH endpoint failed.
export const showApiError = (err, action = "request") => {
  const url = err?.__failedUrl || err?.config?.url || "?";
  const status = err?.response?.status || err?.code || "?";
  const detail = apiErrMsg(err);
  toast.error(`${action} failed · ${status} · ${url}`, {
    description: (typeof detail === "string" ? detail : "").slice(0, 200),
    duration: 8000,
  });
};

export default api;
